import stripe from 'libraries/stripe'
import { get } from 'lodash'
import StripeCoupon from './StripeCoupon';
import StripeSubscriptionItem from './StripeSubscriptionItem';
import StripePlan from 'models/data/stripe/StripePlan'

class StripeSubscription {
  static NUM_DAYS_IN_TRIAL = 7;
  static TAX_PERCENT = 0;

  static statuses = {
    active: "active",
    trialing: "trialing",
    canceled: "canceled"
  };

  static id = (stripeSubscription: any) => get(stripeSubscription, "id", "");

  static status = (stripeSubscription: any) =>
    get(stripeSubscription, "status", "");

  static endsAt = (stripeSubscription: any) =>
    get(stripeSubscription, "current_period_end", 0);

  static isTrialingOrActive = (stripeSubscription: any) =>
    StripeSubscription.status(stripeSubscription) ===
      StripeSubscription.statuses.active ||
    StripeSubscription.status(stripeSubscription) ===
      StripeSubscription.statuses.trialing;

  static cancelAtPeriodEnd = (stripeSubscription: any) =>
    get(stripeSubscription, "cancel_at_period_end", true);

  static async lookupById(id: string) {
    const stripeSubscription = await stripe.subscriptions.retrieve(id);
    return stripeSubscription;
  }

  static async cancelWithId(id: string) {
    await stripe.subscriptions.update(id, { cancel_at_period_end: true });
  }

  static async updatePlan(stripeSubscription: any, newPlan: StripePlan) {
    let stripePlanId = StripePlan.getPlanId(newPlan);
    if (StripePlan.isAnnualPlanId(stripePlanId)) {
      stripePlanId = "annual_72_final";
    }
    const stripeSubscriptionId = StripeSubscription.id(stripeSubscription);
    const updatedStripeSubscription = await stripe.subscriptions.update(
      stripeSubscriptionId,
      {
        cancel_at_period_end: false,
        items: [
          {
            id: StripeSubscription.firstItemId(stripeSubscription),
            plan: stripePlanId,
          },
        ],
        tax_rates: [],
      }
    );
    return updatedStripeSubscription;
  }

  static async createOrUpdate(
    customerId: string,
    planId: string,
    stripeCouponId: string,
    isTrial: boolean
  ) {
    const newSubscription = await StripeSubscription.create(
      customerId,
      planId,
      stripeCouponId,
      isTrial
    );
    return newSubscription;
  }

  static async update(
    subscription: any,
    planId: string,
    stripeCouponId: string
  ) {
    const newCouponId = StripeCoupon.idForPlanId(stripeCouponId, planId);
    const subscriptionId = StripeSubscription.id(subscription);

    const subscriptionItem = StripeSubscription.firstItem(subscription);
    const itemId = StripeSubscriptionItem.id(subscriptionItem);

  let stripePlanId = planId;
  if (StripePlan.isAnnualPlanId(stripePlanId)) {
    stripePlanId = "annual_72_final";
  }

    const updatedSubscription = await stripe.subscriptions.update(
      subscriptionId,
      {
        coupon: newCouponId,
        items: [
          {
            id: itemId,
            plan: planId,
          },
        ],
        tax_rates: [],
      }
    );
    return updatedSubscription;
  }

  static async create(
    customerId: string,
    planId: string,
    stripeCouponId: string,
    isTrial: boolean
  ) {
    let stripePlanId = planId;
    if (StripePlan.isAnnualPlanId(stripePlanId)) {
      stripePlanId = "annual_72_final";
    }
    const newSubscription = await stripe.subscriptions.create({
      customer: customerId,
      items: [{ plan: planId }],
      trial_period_days: isTrial ? StripeSubscription.NUM_DAYS_IN_TRIAL : 0,
      tax_percent: StripeSubscription.TAX_PERCENT,
      coupon: stripeCouponId
    });
    return newSubscription;
  }

  static async createOneTime(
    customerId: string,
    planId: string,
    stripeCouponId: string,
    isTrial: boolean
  ) {
    let stripePlanId = planId;
    if (StripePlan.isAnnualPlanId(stripePlanId)) {
      stripePlanId = "annual_72_final";
    }
    const newSubscription = await stripe.subscriptions.create({
      customer: customerId,
      items: [{ plan: planId }],
      trial_period_days: 0,
      tax_percent: StripeSubscription.TAX_PERCENT,
      coupon: stripeCouponId,
      cancel_at_period_end: true
    });
    return newSubscription;
  }

  static items = stripeSubscription =>
    get(stripeSubscription, "items.data", []);

  static firstItem = stripeSubscription => {
    const [subscriptionItem] = StripeSubscription.items(stripeSubscription);
    return subscriptionItem;
  };

  static firstItemId = (stripeSubscription: any) =>
    get(StripeSubscription.firstItem(stripeSubscription), "id", "");

  static planId = (stripeSubscription: any) => {
    const firstItem = StripeSubscription.firstItem(stripeSubscription);
    const planId = get(firstItem, "plan", "");
    return planId;
  };
}

export default StripeSubscription
